'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useState } from 'react';
import { createTimeframeRenderedFrameStore, isTimeframeViewportTransitionComplete, rememberCommittedTimeframeRenderedFrame, resolveTimeframeRenderedFrame, type TimeframeRenderedFrame } from '@/lib/chart/timeframe-rendered-frame';
import type { ChartInterval } from '@/lib/chart/types';
interface UseWinnerTimeframeFrameArgs<T> {
  readonly interval: ChartInterval;
  readonly targetFrame: TimeframeRenderedFrame<T>;
  readonly targetReady: boolean;
  readonly commitInterval: (interval: ChartInterval) => void;
}
interface WinnerTimeframeFrameResult<T> {
  readonly renderedFrame: TimeframeRenderedFrame<T> | null;
  readonly handleIntervalChange: (interval: ChartInterval) => void;
  readonly handleWindowTransitionComplete: (windowSecs: number) => void;
}
export function useWinnerTimeframeFrame(t0) {
  const $ = _c(25);
  const {
    interval,
    targetFrame,
    targetReady,
    commitInterval
  } = t0;
  const [renderedFrameStore] = useState(_temp);
  const [capturedFrame, setCapturedFrame] = useState(null);
  const [transitionGeneration, setTransitionGeneration] = useState(0);
  const [completedTransitionGeneration, setCompletedTransitionGeneration] = useState(null);
  const retainOutgoingData = targetReady && capturedFrame?.seriesKey === targetFrame.seriesKey && capturedFrame.windowSecs > targetFrame.windowSecs;
  let t1;
  if ($[0] !== capturedFrame || $[1] !== completedTransitionGeneration || $[2] !== renderedFrameStore || $[3] !== retainOutgoingData || $[4] !== targetFrame || $[5] !== targetReady || $[6] !== transitionGeneration) {
    const viewportTransitionComplete = isTimeframeViewportTransitionComplete({
      retainOutgoingData,
      transitionGeneration,
      completedTransitionGeneration
    });
    t1 = resolveTimeframeRenderedFrame({
      target: targetFrame,
      targetReady,
      capturedFrame: targetReady ? capturedFrame : renderedFrameStore.get(),
      viewportTransitionComplete
    });
    $[0] = capturedFrame;
    $[1] = completedTransitionGeneration;
    $[2] = renderedFrameStore;
    $[3] = retainOutgoingData;
    $[4] = targetFrame;
    $[5] = targetReady;
    $[6] = transitionGeneration;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const renderedFrame = t1;
  let t2;
  if ($[8] !== renderedFrame || $[9] !== renderedFrameStore || $[10] !== targetFrame) {
    t2 = () => {
      if (renderedFrame === null) {
        return;
      }
      rememberCommittedTimeframeRenderedFrame(renderedFrameStore, renderedFrame, targetFrame);
    };
    $[8] = renderedFrame;
    $[9] = renderedFrameStore;
    $[10] = targetFrame;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useLayoutEffect(t2);
  let t3;
  if ($[12] !== commitInterval || $[13] !== interval || $[14] !== renderedFrameStore || $[15] !== targetFrame) {
    t3 = nextInterval => {
      if (nextInterval === interval) {
        return;
      }
      const currentFrame = renderedFrameStore.get();
      setCapturedFrame(currentFrame?.seriesKey === targetFrame.seriesKey ? currentFrame : null);
      setTransitionGeneration(_temp2);
      commitInterval(nextInterval);
    };
    $[12] = commitInterval;
    $[13] = interval;
    $[14] = renderedFrameStore;
    $[15] = targetFrame;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleIntervalChange = t3;
  let t4;
  if ($[17] !== retainOutgoingData || $[18] !== targetFrame || $[19] !== transitionGeneration) {
    t4 = completedWindowSecs => {
      if (!retainOutgoingData || completedWindowSecs !== targetFrame.windowSecs) {
        return;
      }
      setCompletedTransitionGeneration(transitionGeneration);
    };
    $[17] = retainOutgoingData;
    $[18] = targetFrame;
    $[19] = transitionGeneration;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const handleWindowTransitionComplete = t4;
  let t5;
  if ($[21] !== handleIntervalChange || $[22] !== handleWindowTransitionComplete || $[23] !== renderedFrame) {
    t5 = {
      renderedFrame,
      handleIntervalChange,
      handleWindowTransitionComplete
    };
    $[21] = handleIntervalChange;
    $[22] = handleWindowTransitionComplete;
    $[23] = renderedFrame;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}
function _temp2(generation) {
  return generation + 1;
}
function _temp() {
  return createTimeframeRenderedFrameStore();
}